import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  useWebAnalyticsSites,
  useCreateWebAnalyticsSite,
  useUpdateWebAnalyticsSite,
  useDeactivateWebAnalyticsSite,
} from '../../hooks/useWebAnalyticsSites';
import { Button } from '../../components/common/Button';
import { Spinner } from '../../components/common/Spinner';
import { ConfirmModal } from '../../components/common/Modal';

export default function WebAnalyticsSitesAdminPage() {
  const navigate = useNavigate();
  const { data: sites, isLoading } = useWebAnalyticsSites();
  const createMutation = useCreateWebAnalyticsSite();
  const updateMutation = useUpdateWebAnalyticsSite();
  const deactivateMutation = useDeactivateWebAnalyticsSite();

  const [showForm, setShowForm] = useState(false);
  const [hostname, setHostname] = useState('');
  const [productName, setProductName] = useState('');
  const [canonicalStreamId, setCanonicalStreamId] = useState('');
  const [formError, setFormError] = useState<string | null>(null);

  const [editingId, setEditingId] = useState<number | null>(null);
  const [editingProductName, setEditingProductName] = useState('');
  const [editingCanonicalStreamId, setEditingCanonicalStreamId] = useState('');
  const [editingActive, setEditingActive] = useState(true);
  const [editError, setEditError] = useState<string | null>(null);

  const [deactivateId, setDeactivateId] = useState<number | null>(null);
  const [deactivateError, setDeactivateError] = useState<string | null>(null);

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    setFormError(null);
    try {
      await createMutation.mutateAsync({
        hostname: hostname.trim(),
        productName: productName.trim(),
        canonicalStreamId: canonicalStreamId.trim() ? canonicalStreamId.trim() : null,
      });
      setShowForm(false);
      setHostname('');
      setProductName('');
      setCanonicalStreamId('');
    } catch (err) {
      setFormError((err as Error).message);
    }
  };

  const handleUpdate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (editingId === null) return;
    setEditError(null);
    try {
      await updateMutation.mutateAsync({
        id: editingId,
        input: {
          productName: editingProductName.trim(),
          canonicalStreamId: editingCanonicalStreamId.trim() ? editingCanonicalStreamId.trim() : null,
          active: editingActive,
        },
      });
      setEditingId(null);
      setEditingProductName('');
      setEditingCanonicalStreamId('');
    } catch (err) {
      setEditError((err as Error).message);
    }
  };

  const handleDeactivate = async () => {
    if (deactivateId === null) return;
    setDeactivateError(null);
    try {
      await deactivateMutation.mutateAsync(deactivateId);
      setDeactivateId(null);
    } catch (err) {
      setDeactivateError((err as Error).message);
    }
  };

  if (isLoading) {
    return <div className="flex justify-center py-8"><Spinner /></div>;
  }

  return (
    <div className="space-y-6">
      <div>
        <button onClick={() => navigate('/admin')} className="text-sm text-gray-500 hover:text-gray-700 mb-1">
          &larr; Back to Admin
        </button>
        <div className="flex items-center justify-between">
          <h1 className="text-2xl font-semibold text-gray-900">Web Analytics Sites</h1>
          <Button size="sm" onClick={() => setShowForm(!showForm)}>
            {showForm ? 'Cancel' : 'Add Site'}
          </Button>
        </div>
        <p className="mt-1 text-sm text-gray-500">
          These hostnames drive the Web Analytics report. A hostname not listed here is
          excluded from the report entirely. Leaving the canonical GA4 stream empty means
          the hostname is queried without a stream filter — the difference between
          deduplicated and raw numbers for that site.
        </p>
      </div>

      {showForm && (
        <form onSubmit={handleCreate} className="bg-white rounded-lg border border-gray-200 p-4 space-y-3">
          {formError && <p className="text-sm text-red-600">{formError}</p>}
          <div className="flex gap-3 items-center">
            <input
              type="text"
              required
              value={hostname}
              onChange={(e) => setHostname(e.target.value)}
              placeholder="Hostname *"
              className="flex-1 rounded-lg border border-gray-300 px-3 py-2 text-sm"
            />
            <input
              type="text"
              required
              value={productName}
              onChange={(e) => setProductName(e.target.value)}
              placeholder="Product name *"
              className="flex-1 rounded-lg border border-gray-300 px-3 py-2 text-sm"
            />
            <input
              type="text"
              value={canonicalStreamId}
              onChange={(e) => setCanonicalStreamId(e.target.value)}
              placeholder="Canonical GA4 stream ID (optional)"
              className="w-64 rounded-lg border border-gray-300 px-3 py-2 text-sm"
            />
            <Button type="submit" size="sm" isLoading={createMutation.isPending}>Create</Button>
          </div>
        </form>
      )}

      <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Hostname</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Product</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Canonical Stream</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Active</th>
              <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {sites?.map((site) => (
              <tr key={site.id} className="hover:bg-gray-50">
                {editingId === site.id ? (
                  <>
                    <td className="px-6 py-3 text-sm text-gray-500">{site.hostname}</td>
                    <td className="px-6 py-2" colSpan={3}>
                      <form onSubmit={handleUpdate} className="flex gap-2 items-center flex-wrap">
                        {editError && <p className="w-full text-sm text-red-600">{editError}</p>}
                        <input
                          type="text"
                          required
                          value={editingProductName}
                          onChange={(e) => setEditingProductName(e.target.value)}
                          placeholder="Product name"
                          className="flex-1 rounded border border-gray-300 px-2 py-1 text-sm"
                          autoFocus
                        />
                        <input
                          type="text"
                          value={editingCanonicalStreamId}
                          onChange={(e) => setEditingCanonicalStreamId(e.target.value)}
                          placeholder="Canonical stream ID"
                          className="w-56 rounded border border-gray-300 px-2 py-1 text-sm"
                        />
                        <label className="flex items-center gap-1 text-sm text-gray-600">
                          <input
                            type="checkbox"
                            checked={editingActive}
                            onChange={(e) => setEditingActive(e.target.checked)}
                            className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                          />
                          Active
                        </label>
                        <Button type="submit" size="sm" isLoading={updateMutation.isPending}>Save</Button>
                        <Button type="button" size="sm" variant="ghost" onClick={() => setEditingId(null)}>Cancel</Button>
                      </form>
                    </td>
                    <td></td>
                  </>
                ) : (
                  <>
                    <td className="px-6 py-3 text-sm font-medium text-gray-900">{site.hostname}</td>
                    <td className="px-6 py-3 text-sm text-gray-500">{site.productName}</td>
                    <td className="px-6 py-3 text-sm text-gray-500">{site.canonicalStreamId ?? '—'}</td>
                    <td className="px-6 py-3 text-sm text-gray-500">{site.active ? 'Active' : 'Inactive'}</td>
                    <td className="px-6 py-3 text-right space-x-3">
                      <button
                        onClick={() => {
                          setEditingId(site.id);
                          setEditingProductName(site.productName);
                          setEditingCanonicalStreamId(site.canonicalStreamId ?? '');
                          setEditingActive(site.active);
                          setEditError(null);
                        }}
                        className="text-primary-600 hover:text-primary-800 text-sm font-medium"
                      >
                        Edit
                      </button>
                      <button
                        onClick={() => { setDeactivateId(site.id); setDeactivateError(null); }}
                        className="text-red-600 hover:text-red-800 text-sm font-medium"
                      >
                        Deactivate
                      </button>
                    </td>
                  </>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <ConfirmModal
        isOpen={deactivateId !== null}
        onClose={() => setDeactivateId(null)}
        onConfirm={handleDeactivate}
        title="Deactivate Site"
        message="Are you sure you want to deactivate this site? It will stop counting toward the report until reactivated from the edit row."
        confirmText="Deactivate"
        variant="danger"
        isLoading={deactivateMutation.isPending}
        error={deactivateError}
      />
    </div>
  );
}
